import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useVentureInvites } from '@/hooks/useVentureInvites';
import { Link2, Copy, CheckCircle2, Clock, Users, UserPlus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export const VentureInviteTab = () => {
  const { invites, invitedUsers, loading, generating, generateInvite, processInviteCode } = useVentureInvites();
  const [linkCode, setLinkCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleGenerateRegistrationInvite = async () => {
    const result = await generateInvite('registration');
    if (result) {
      copyToClipboard(result.invite_url, 'registration-new');
    }
  };

  const handleGenerateLinkInvite = async () => {
    const result = await generateInvite('link_existing');
    if (result) {
      copyToClipboard(result.invite_code, 'link-new');
    }
  };

  const handleProcessCode = async () => {
    if (!linkCode.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an invite code',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    const success = await processInviteCode(linkCode.trim());
    if (success) {
      setLinkCode('');
    }
    setProcessing(false);
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast({
      title: 'Copied!',
      description: 'Copied to clipboard',
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const activeInvites = invites.filter(inv => !inv.used_at && new Date(inv.expires_at) > new Date());
  const usedInvites = invites.filter(inv => inv.used_at);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading invites...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generate Invites Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* New User Registration Invite */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Invite New Users
            </CardTitle>
            <CardDescription>
              Generate a registration link to invite new users to join the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleGenerateRegistrationInvite}
              disabled={generating}
              className="w-full"
            >
              {generating ? 'Generating...' : 'Generate Registration Link'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Share this link with potential users. When they register, they'll be automatically linked to your account.
            </p>
          </CardContent>
        </Card>

        {/* Existing User Link Code */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link Existing Users
            </CardTitle>
            <CardDescription>
              Generate a code for existing users to link their accounts to yours
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleGenerateLinkInvite}
              disabled={generating}
              variant="outline"
              className="w-full"
            >
              {generating ? 'Generating...' : 'Generate Link Code'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Share this 8-character code with existing users. They can enter it in their profile settings.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Enter Link Code Section */}
      <Card>
        <CardHeader>
          <CardTitle>Have an Invite Code?</CardTitle>
          <CardDescription>
            Enter an invite code to link your account to another user
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="link-code" className="sr-only">
                Invite Code
              </Label>
              <Input
                id="link-code"
                placeholder="Enter 8-character code"
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                maxLength={8}
              />
            </div>
            <Button onClick={handleProcessCode} disabled={processing || !linkCode.trim()}>
              {processing ? 'Processing...' : 'Link Account'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Invites */}
      {activeInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Active Invites ({activeInvites.length})
            </CardTitle>
            <CardDescription>
              Your currently active invitation links and codes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={invite.invite_type === 'registration' ? 'default' : 'secondary'}>
                        {invite.invite_type === 'registration' ? 'Registration Link' : 'Link Code'}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Expires {format(new Date(invite.expires_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {invite.invite_type === 'registration' ? invite.invite_url : invite.invite_code}
                    </code>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      copyToClipboard(
                        invite.invite_type === 'registration' ? invite.invite_url : invite.invite_code,
                        invite.id
                      )
                    }
                  >
                    {copiedId === invite.id ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invited Users */}
      {invitedUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Linked Users ({invitedUsers.length})
            </CardTitle>
            <CardDescription>
              Users who have joined or linked through your invitations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invitedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{user.display_name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Joined {format(new Date(user.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Used Invites */}
      {usedInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Used Invites ({usedInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {usedInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-2 border rounded text-sm"
                >
                  <code className="font-mono">{invite.invite_code}</code>
                  <span className="text-muted-foreground">
                    Used {format(new Date(invite.used_at!), 'MMM d, yyyy')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
